import React, { useRef, useState } from "react";
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";  
export default function PhotoAnalysis() {
  const fileInputRef = useRef(null);

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-qureo.onrender.com';
  const [image, setImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const {profile} = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleChooseFile = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError("");
    setResult(null);

    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setError("Unsupported file type. Please upload JPG, PNG, or GIF.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size exceeds 10MB limit.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
    setSelectedFile(file);
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      setError("Please choose a photo first.");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("image", selectedFile);

      const response = await fetch(`${API_BASE}/api/gpt/analyze-image`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || "Unable to analyze photo right now.");
      }

      setResult({
        message: data?.message || "Analysis completed.",
        triage: data?.triage || "Routine, book soon",
        conditionClusters: data?.conditionClusters || null,
        actionPlan: data?.actionPlan || null,
      });
    } catch (err) {
      setError(err.message || "Failed to analyze photo. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        background: "var(--bg)",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div style={{ width: "100%", maxWidth: 420, padding: 20 }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 20,
            color: "#fff",
          }}
        >
           <i 
          className="fi fi-br-angle-left"
          onClick={() => navigate(-1)}
      
          style={  {
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    color : "black",

    fontSize: '20px',
  }}
        ></i>
       

          <div style={{ flex: 1, color : "black", fontSize: 18, textAlign: "center", fontWeight: 600 }}>
            Health Assistant
          </div>
       <div
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        overflow: "hidden",
        background: "var(--surface)",
        boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
        flexShrink: 0,
      }}
    >
      <img
        src={profile?.avatar || "/avatar.png"}
        alt="Profile"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
          onClick={()=>{navigate("/profile")}}
      />
    </div>
        </div>

        {/* Main Card */}
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 18,
            padding: 20,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          }}
        >
          {/* Title */}
          <div style={{ display: "flex", marginBottom: 16 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                backgroundColor: "var(--primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: 12,
              }}
            >
              <CameraAltOutlinedIcon style={{ color: "primary" }} />
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                Photo Analysis
              </div>
              <div style={{ fontSize: 14, color: "#777" }}>
                Upload a photo for AI-powered health insights
              </div>
            </div>
          </div>

          {/* Upload Box */}
          <div
            style={{
              border: "2px dashed #ddd",
              borderRadius: 14,
              padding: 20,
              textAlign: "center",
              marginBottom: 16,
            }}
          >
            {!image && <div style={{ fontSize: 40 }}><CameraAltOutlinedIcon style={{ color: "var(--primary)" }} /></div>}

            {image && (
              <img
                src={image}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  borderRadius: 10,
                  marginBottom: 12,
                }}
              />
            )}

            <button
              onClick={handleChooseFile}
              style={{
                padding: "10px 16px",
                borderRadius: 10,
                border: "1px solid #ddd",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Upload photo for analysis. 
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {error && (
              <div style={{ color: "red", fontSize: 12, marginTop: 8 }}>
                {error}
              </div>
            )}
          </div>

          {/* Analyze Button */}
          {image && (
            <button
              onClick={handleAnalyze}
              disabled={loading}
              style={{
                width: "100%",
                padding: 12,
                borderRadius: 12,
                border: "none",
                backgroundColor: "var(--primary)",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: 16,
              }}
            >
              {loading ? "Analyzing..." : "Analyze Photo"}
            </button>
          )}

          {/* Result */}
          {result && (
            <div
              style={{
                backgroundColor: "#f0f7ff",
                borderRadius: 12,
                padding: 14,
                fontSize: 14,
              }}
            >
              <div style={{ marginBottom: 8, whiteSpace: "pre-wrap" }}>{result.message}</div>

              {result.triage && (
                <div style={{ marginBottom: 8 }}>
                  <strong>Urgency:</strong> {result.triage}
                </div>
              )}

              {result.actionPlan?.doNow?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <strong>Do now:</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                    {result.actionPlan.doNow.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.actionPlan?.escalate?.length > 0 && (
                <div>
                  <strong>Escalate if:</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 18 }}>
                    {result.actionPlan.escalate.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Notice */}
          <div
            style={{
              backgroundColor: "#f8f9fb",
              borderRadius: 12,
              padding: 16,
              marginTop: 16,
              fontSize: 13,
              color: "#666",
            }}
          >
            <strong>Important Notice</strong>
            <br />
            This AI analysis is for informational purposes only and not medical
            advice.
          </div>
        </div>
      </div>
    </div>
  );
}
